import { useLocation, useNavigate } from "react-router";

import { t } from "i18next";

import { Container, Grid, SxProps, Typography } from "@mui/material";
import { Box } from "@mui/material";
import type { Theme } from "@mui/material/styles";

import { schemeSx } from "@/src/theme/schemeStyles";

import { userAuctionsBadgesAtom } from "@/src/state/userAuctionsBadgesAtom";

import { BaanaLogo } from "@/src/components/atoms";
import FooterLink from "@/src/components/atoms/FooterLink/FooterLink";

import ContentRouterWidget from "@almamedia/ict-content-router-react";

import { LoginSource } from "@/src/auth/auth";
import { useAuth } from "@/src/auth/hooks/useAuth";
import { getBaseUrl } from "@/src/utils/baseUrlUtils";
import {
  CITY_LANDING_PAGES,
  MAKER_LANDING_PAGES,
  WE_BUY_CARS_PATH,
  toLandingPath
} from "@/src/utils/weBuyCarsLandingPages";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useAtom } from "jotai";

const AlmaFooter = ContentRouterWidget;

// The Alma footer widget ships its own stylesheet with a hardcoded background
// that does not react to our light/dark theme, so the wrapper mirrors it.
const ALMA_FOOTER_BACKGROUND = "#333333";

const Footer = () => {
  const { isAuthenticated, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const gtmSendEvent = useGTMDispatch();
  const [userAuctionsBadges] = useAtom(userAuctionsBadgesAtom);
  const draftAuction = userAuctionsBadges?.draftAuctionId;

  const onClickPlaceBid = () => {
    if (!isAuthenticated) {
      gtmSendEvent({
        event: "frontpageLoginClicked",
        value: "consumer"
      });
      login("consumer", LoginSource.FOOTER_CTA);
      // If logged in and there is a draft, direct to editing draft
    } else if (isAuthenticated && draftAuction) {
      navigate("/auction/edit/" + draftAuction);
      // If logged in but no draft, direct to new auction
      // Users can have multiple auctions, so we don't block based on active auctions
    } else if (isAuthenticated && !draftAuction) {
      navigate("/auction/edit");
    }
  };

  let placeBidButtonText = t("components.footer.placeABid");
  if (draftAuction) {
    placeBidButtonText = t("components.footer.continueAuction");
  }

  // Show footer only for consumer and dealer users
  if (location.pathname.startsWith("/admin")) {
    return null;
  }

  const isDealer = location.pathname.startsWith("/dealer");
  const showAlmaFooter = !(isDealer && isAuthenticated);

  const headingStyles: SxProps<Theme> = schemeSx((muiTheme) => ({
    mb: 1.5,
    lineHeight: "1.25rem",
    color: muiTheme.vars.palette.common.white,
    ...muiTheme.applyStyles("dark", {
      color: muiTheme.vars.palette.text.primary
    })
  }));

  return (
    <>
      <Container
        component="footer"
        maxWidth={false}
        // Light mode keeps the dark gradient band with white content; dark mode
        // shares the page surface the home page FAQ section sits on. Written
        // with `theme.vars` and `applyStyles` so the prerendered markup is the
        // same in both schemes and the band is right before hydration.
        sx={schemeSx((muiTheme) => ({
          justifyContent: "center",
          backgroundImage: `linear-gradient(${muiTheme.vars.palette.text.primary} 0%, ${muiTheme.vars.palette.text.primary} 50%, ${muiTheme.vars.palette.semantic.secondary.main} 85%, ${muiTheme.vars.palette.semantic.secondary.main} 90%, ${muiTheme.vars.palette.common.black} 100%)`,
          px: 4,
          py: 3,
          borderBottom: "2px solid",
          borderColor: muiTheme.vars.palette.common.white,
          ...muiTheme.applyStyles("dark", {
            backgroundColor: muiTheme.vars.palette.background.default,
            backgroundImage: "none",
            borderColor: muiTheme.vars.palette.misc.outlineBorder
          })
        }))}
      >
        <Grid sx={{ display: "flex", justifyContent: "center" }}>
          <Grid
            container
            spacing={2}
            sx={{ width: "100%", maxWidth: "100rem" }}
          >
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 2
              }}
            >
              <Box
                sx={schemeSx((theme) => ({
                  pr: 3,
                  maxWidth: "10rem",
                  color: theme.vars.palette.common.white,
                  ...theme.applyStyles("dark", {
                    color: theme.vars.palette.text.primary
                  }),
                  ">svg": {
                    borderRight: [
                      "none",
                      `2px solid ${theme.palette.semantic.primary.main}`
                    ],
                    pr: 2,
                    pt: 0.25
                  }
                }))}
              >
                <BaanaLogo color="currentColor" />
              </Box>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 2
              }}
            >
              <Typography variant="h3" sx={headingStyles}>
                {t("components.footer.aboutUsHeader")}
              </Typography>
              <FooterLink
                text={t("components.footer.contact")}
                href={
                  isDealer
                    ? `${getBaseUrl()}/dealer/contact-info`
                    : `${getBaseUrl()}/contact-info`
                }
              />
              <FooterLink
                text={t("components.footer.aboutUs")}
                href="https://www.almamobility.fi/tuotteet/baana/"
                newTab
              />
              <FooterLink
                text={t("components.footer.termsOfUse")}
                href="https://www.almamedia.fi/markkinapaikkaehdot/"
                newTab
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 2
              }}
            >
              <Typography variant="h3" sx={headingStyles}>
                {t("components.footer.baanaHeader")}
              </Typography>
              {!isDealer && (
                <FooterLink
                  text={placeBidButtonText}
                  onClick={onClickPlaceBid}
                />
              )}
              <FooterLink
                text={
                  isDealer
                    ? t("components.footer.forConsumers")
                    : t("components.footer.forDealers")
                }
                href={isDealer ? `${getBaseUrl()}/` : `${getBaseUrl()}/dealer`}
              />
              {isDealer ? (
                <FooterLink
                  text={t("components.footer.userGuide")}
                  href="assets/docs/Baana_kayttoohje_autoliikkeille.pdf"
                  sx={{
                    display: "none"
                  }}
                />
              ) : (
                <FooterLink
                  text={t("components.footer.faq")}
                  href={`${getBaseUrl()}/faq`}
                />
              )}
            </Grid>

            {!isDealer && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 3
                  }}
                >
                  <Typography variant="h3" sx={headingStyles}>
                    {t("components.footer.weBuyCars")}
                  </Typography>
                  <FooterLink
                    text={t("components.footer.weBuyCars")}
                    href={`${getBaseUrl()}${WE_BUY_CARS_PATH}`}
                  />
                  {CITY_LANDING_PAGES.map(({ slug, cityName }) => (
                    <FooterLink
                      key={slug}
                      text={t("components.footer.weBuyCarsLink", {
                        name: cityName.basic
                      })}
                      href={`${getBaseUrl()}${toLandingPath(slug)}`}
                    />
                  ))}
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 3
                  }}
                >
                  <Typography variant="h3" sx={headingStyles}>
                    {t("components.footer.weBuyCarsByMaker")}
                  </Typography>
                  {MAKER_LANDING_PAGES.map(({ slug, carMaker }) => (
                    <FooterLink
                      key={slug}
                      text={t("components.footer.weBuyCarsLink", {
                        name: carMaker
                      })}
                      href={`${getBaseUrl()}${toLandingPath(slug)}`}
                    />
                  ))}
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Container>
      {showAlmaFooter && (
        <Container
          component="footer"
          maxWidth={false}
          sx={{
            backgroundColor: ALMA_FOOTER_BACKGROUND,
            p: [2, 3]
          }}
        >
          <Container>
            <AlmaFooter slot="ALMA_FOOTER" />
          </Container>
        </Container>
      )}
    </>
  );
};

export default Footer;
